/**
 * RankCrown — Stream H (R4)
 *
 * Farbcodierte Krone + Rangzahl + Perzentil, wie in v1 (user_hotspots/index.haml).
 * Wird sowohl in der Hero-Leiste der Detailseite als auch in der Rank-Spalte der
 * Liste verwendet, damit Farbskala und Fallback nur an einer Stelle leben.
 */
import { useTranslation } from 'react-i18next'
import { rankColor, rankTier, formatTopPercent } from './rankColor'
import { activeBcp47 } from '../../lib/locale'

type Props = {
  rank: number | null | undefined
  percentile: number | null | undefined
  /** Grundgesamtheit (rank_total) — nur fuer den Tooltip. */
  total?: number | null
  /** Kompakt: nur Krone + Rangzahl, ohne Perzentil-Zeile. */
  compact?: boolean
  'data-testid'?: string
}

function CrownIcon({ color }: { color: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path
        d="M1.5 5.2l2.6 2.1 2.6-4 2.6 4 2.6-2.1-1 7.3H2.5l-1-7.3z"
        fill={color}
        stroke={color}
        strokeWidth="0.8"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function RankCrown({ rank, percentile, total, compact = false, ...rest }: Props) {
  const { t } = useTranslation('hotspots')
  const color = rankColor(percentile)
  const tier = rankTier(percentile)
  const pct = formatTopPercent(percentile)

  const title = rank == null
    ? t('rank.tooltipNa')
    : total != null
      ? t('rank.tooltipWithTotal', { rank, total })
      : t('rank.tooltip')

  return (
    <span
      className="rank-crown"
      title={title}
      data-tier={tier}
      data-testid={rest['data-testid'] ?? 'hotspot-rank'}
      style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 1, lineHeight: 1.2 }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <CrownIcon color={color} />
        <span className="num-sm" style={{ fontWeight: 600 }}>
          {rank == null ? t('rank.na') : `#${rank.toLocaleString(activeBcp47())}`}
        </span>
      </span>
      {!compact && pct != null && (
        <span className="muted" style={{ fontSize: 10 }}>
          {t('rank.percentileSuffix', { value: pct })}
        </span>
      )}
    </span>
  )
}
